import React, { useState, useRef } from 'react';
import { reviewsData, ReviewItem } from '../../data/reviewsData';

export function ReviewsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Refs for swipe detection
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50; // px

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) < minSwipeDistance) return;

    if (distance > 0) {
      // swipe left -> next
      setActiveIndex((prev) => (prev < reviewsData.length - 1 ? prev + 1 : 0));
    } else {
      // swipe right -> previous
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : reviewsData.length - 1));
    }
  };

  // Render star icons
  const renderStars = (rating: number) => (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg
          key={i}
          className={`w-4 h-4 ${i <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.97a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.388 2.463a1 1 0 00-.364 1.118l1.287 3.97c.3.921-.755 1.688-1.54 1.118l-3.388-2.463a1 1 0 00-1.175 0l-3.388 2.463c-.784.57-1.838-.197-1.54-1.118l1.287-3.97a1 1 0 00-.364-1.118L2.045 9.397c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 00.95-.69l1.286-3.97z" />
        </svg>
      ))}
    </div>
  );

  return (
    <section className="py-12 bg-gray-50">
      {/* Header */}
      <div className="text-center mb-8 px-4">
        <h2 className="text-3xl font-semibold text-gray-700">
          Parent & Carer Reviews
        </h2>
        <p className="mt-2 text-gray-600">
          From Daynurseries.co.uk reviews, see what parents say about Great Child Nurseries.
        </p>
      </div>

      {/* Desktop Grid */}
      {!isMobile && (
        <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4 max-w-6xl mx-auto">
          {reviewsData.map((review: ReviewItem) => (
            <div
              key={review.id}
              className="bg-white rounded-lg shadow p-6 flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-purple-800">
                  {review.author}
                </h3>
                {renderStars(review.rating)}
              </div>
              <time className="text-sm text-gray-500 mb-4">{review.date}</time>
              <p className="text-gray-600 flex-1">{review.content}</p>
            </div>
          ))}
        </div>
      )}

      {/* Mobile Carousel with swipe */}
      {isMobile && (
        <div
          className="md:hidden px-4"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="bg-white rounded-lg shadow mx-auto max-w-sm p-6">
            <h3 className="text-lg font-medium text-purple-800 mb-2">
              {reviewsData[activeIndex].author}
            </h3>
            {renderStars(reviewsData[activeIndex].rating)}
            <time className="text-sm text-gray-500 block mb-4">
              {reviewsData[activeIndex].date}
            </time>
            <p className="text-gray-600 mb-4">
              {reviewsData[activeIndex].content}
            </p>
            <div className="flex justify-center space-x-2">
              {reviewsData.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    idx === activeIndex ? 'bg-purple-800' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to review ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
